import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Cell, Button, Spinner,
  Text, Caption, Headline, Snackbar, IconButton, Checkbox, Input, Tabs, TabsItem,
  ModalRoot, ModalPageHeader, FormItem, Alert,
} from '@vkontakte/vkui';
import { Icon24Add, Icon24Delete, Icon24Write, Icon24Cancel } from '@vkontakte/icons';
import { api } from '../../api/client';
import { useWeddingStore } from '../../stores/wedding';
import type { Task, Subtask, TaskCategory, MessageAttachment, UploadedAttachment } from '../../types';
import { DragHandle } from '../../ui/DragHandle';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';
import { AttachmentView } from '../../ui/AttachmentView';
import { CatBadge } from '../../ui/CatBadge';
import '../../styles/tasks-premium.css';

function parseDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

type SubtaskForm = { name: string; note: string; is_complete: boolean };
const emptySubtask: SubtaskForm = { name: '', note: '', is_complete: false };

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentWedding, weddingsLoaded, members, loadMembers } = useWeddingStore();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [task, setTask] = useState<Task | null>(null);
  const [category, setCategory] = useState<TaskCategory | null>(null);
  const [subs, setSubs] = useState<Subtask[]>([]);
  const [tab, setTab] = useState<'task' | 'subtasks'>('task');
  const [snack, setSnack] = useState<string | null>(null);

  const [openToken, setOpenToken] = useState(0);
  const [modal, setModal] = useState<'subtask' | null>(null);
  const [editingSub, setEditingSub] = useState<Subtask | null>(null);
  const [form, setForm] = useState<SubtaskForm>(emptySubtask);
  const [atts, setAtts] = useState<MessageAttachment[]>([]);
  const [attBusy, setAttBusy] = useState(false);
  const [confirmAtt, setConfirmAtt] = useState<MessageAttachment | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!currentWedding || !id) return;
    setLoading(true);
    try {
      const t = await api.get<Task>(`weddings/${currentWedding.id}/tasks/${id}`);
      const [subsList, cats, attsList] = await Promise.all([
        api.get<Subtask[]>(`weddings/${currentWedding.id}/tasks/${id}/subtasks`),
        api.get<TaskCategory[]>(`weddings/${currentWedding.id}/tasks/categories`),
        api.get<MessageAttachment[]>(`weddings/${currentWedding.id}/tasks/${id}/attachments`),
      ]);
      setTask(t);
      setSubs(subsList);
      setAtts(attsList);
      setCategory(t.category_id ? cats.find((c) => c.id === t.category_id) || null : null);
    } catch {
      setLoadError(true);
      setSnack('Не удалось загрузить задачу');
    } finally {
      setLoading(false);
    }
  }, [currentWedding, id]);

  useEffect(() => {
    if (!currentWedding) {
      if (weddingsLoaded) navigate('/');
      return;
    }
    void load();
    void loadMembers();
  }, [currentWedding, weddingsLoaded, load, loadMembers, navigate]);

  function memberName(mid?: string) {
    if (!mid) return '';
    const m = members.find((x) => x.id === mid);
    if (!m) return '';
    return [m.user_first_name, m.user_last_name].filter(Boolean).join(' ') || 'Участник';
  }

  async function saveSubtask() {
    if (!currentWedding || !id || !form.name.trim()) return;
    try {
      if (editingSub) {
        const updated = await api.patch<Subtask>(
          `weddings/${currentWedding.id}/tasks/${id}/subtasks/${editingSub.id}`,
          { name: form.name.trim(), note: form.note.trim() || undefined, is_complete: form.is_complete },
        );
        setSubs((xs) => xs.map((x) => (x.id === updated.id ? updated : x)));
        setSnack('Подзадача обновлена');
      } else {
        const created = await api.post<Subtask>(
          `weddings/${currentWedding.id}/tasks/${id}/subtasks`,
          { name: form.name.trim(), note: form.note.trim() || undefined },
        );
        if (form.is_complete) {
          const done = await api.patch<Subtask>(
            `weddings/${currentWedding.id}/tasks/${id}/subtasks/${created.id}`,
            { is_complete: true },
          );
          setSubs((xs) => [...xs, done]);
        } else {
          setSubs((xs) => [...xs, created]);
        }
        setSnack('Подзадача добавлена');
      }
      setModal(null);
      setEditingSub(null);
      setForm(emptySubtask);
    } catch {
      setSnack('Ошибка сохранения');
    }
  }

  async function toggleSub(s: Subtask) {
    if (!currentWedding || !id) return;
    try {
      const updated = await api.patch<Subtask>(
        `weddings/${currentWedding.id}/tasks/${id}/subtasks/${s.id}`,
        { is_complete: !s.is_complete },
      );
      setSubs((xs) => xs.map((x) => (x.id === updated.id ? updated : x)));
    } catch {
      setSnack('Ошибка');
    }
  }

  async function deleteSub(s: Subtask) {
    if (!currentWedding || !id) return;
    try {
      await api.delete(`weddings/${currentWedding.id}/tasks/${id}/subtasks/${s.id}`);
      setSubs((xs) => xs.filter((x) => x.id !== s.id));
      setSnack('Подзадача удалена');
    } catch {
      setSnack('Ошибка удаления');
    }
  }

  async function uploadAtts(files: FileList | File[]) {
    if (!currentWedding || !id) return;
    const arr = Array.from(files).slice(0, 10);
    if (!arr.length) return;
    setAttBusy(true);
    try {
      const fd = new FormData();
      for (const f of arr) fd.append('files', f);
      const token = localStorage.getItem('colibri_token');
      const res = await fetch(`/api/weddings/${currentWedding.id}/messages/attachments`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) throw new Error(String(res.status));
      const uploaded = (await res.json()) as UploadedAttachment[];
      const linked = await api.post<MessageAttachment[]>(
        `weddings/${currentWedding.id}/tasks/${id}/attachments`,
        { attachment_ids: uploaded.map((a) => a.id) },
      );
      setAtts((prev) => [...prev, ...linked]);
      setSnack('Файлы добавлены');
    } catch {
      setSnack('Не удалось загрузить файл');
    } finally {
      setAttBusy(false);
    }
  }

  async function deleteAtt(a: MessageAttachment) {
    if (!currentWedding || !id) return;
    try {
      await api.delete(`weddings/${currentWedding.id}/tasks/${id}/attachments/${a.id}`);
      setAtts((prev) => prev.filter((x) => x.id !== a.id));
      setSnack('Вложение удалено');
    } catch {
      setSnack('Ошибка удаления');
    }
  }

  function openCreateSub() {
    setEditingSub(null);
    setForm(emptySubtask);
    setOpenToken((t) => t + 1);
    setModal('subtask');
  }

  function openEditSub(s: Subtask) {
    setEditingSub(s);
    setForm({ name: s.name, note: s.note || '', is_complete: s.is_complete });
    setOpenToken((t) => t + 1);
    setModal('subtask');
  }

  const doneSubs = subs.filter((s) => s.is_complete).length;

  return (
    <Panel className="tp-root">
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/tasks')} />}>
        Задача
      </PanelHeader>

      {loading ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
        </Group>
      ) : loadError || !task ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 32 }}>
            <Text>Задача не найдена</Text>
            <Button size="m" mode="secondary" stretched style={{ marginTop: 16 }} onClick={() => navigate('/tasks')}>
              Вернуться к списку
            </Button>
          </Div>
        </Group>
      ) : (
        <>
          <Group>
            <Div>
              <Headline level="1" style={{ marginBottom: 4 }}>{task.name}</Headline>
              {task.is_complete ? (
                <Caption style={{ color: 'var(--vkui--color_text_positive)' }}>Выполнено</Caption>
              ) : (
                <Caption style={{ color: 'var(--vkui--color_text_secondary)' }}>В ожидании</Caption>
              )}
            </Div>
          </Group>

          <Group>
            <Tabs>
              <TabsItem
                selected={tab === 'task'}
                onClick={() => setTab('task')}
              >
                Задача
              </TabsItem>
              <TabsItem
                selected={tab === 'subtasks'}
                onClick={() => setTab('subtasks')}
                after={<span style={{ opacity: 0.6 }}>{subs.length || undefined}</span>}
              >
                Подзадачи
              </TabsItem>
            </Tabs>
          </Group>

          {tab === 'task' ? (
            <>
            <Group>
              <Cell
                subtitle={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <CatBadge iconId={category?.icon_id} size={20} />
                    {category ? category.name : 'Без категории'}
                  </span>
                }
              >
                Категория
              </Cell>
              <Cell subtitle={task.due_date ? parseDate(task.due_date).toLocaleDateString('ru-RU') : 'Не задан'}>
                Срок
              </Cell>
              <Cell subtitle={task.note || 'Нет заметки'}>
                Заметка
              </Cell>
              <Cell subtitle={memberName(task.assigned_to) || 'Не назначен'}>
                Ответственный
              </Cell>
              <Cell subtitle={new Date(task.updated_at).toLocaleString('ru-RU')}>
                Обновлено
              </Cell>
            </Group>
            <Group header="Вложения">
              <Div>
                <Button size="m" mode="secondary" stretched before={<Icon24Add />} disabled={attBusy} onClick={() => fileRef.current?.click()}>
                  {attBusy ? 'Загрузка…' : 'Добавить файл'}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files) void uploadAtts(e.target.files);
                    e.target.value = '';
                  }}
                />
              </Div>
              {atts.length > 0 && (
                <Div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {atts.map((a) => (
                    <div key={a.id} style={{ position: 'relative' }}>
                      <AttachmentView url={a.file_url} variant="task" />
                      <button
                        type="button"
                        aria-label="Удалить"
                        onClick={() => setConfirmAtt(a)}
                        style={{
                          position: 'absolute', top: -6, right: -6, width: 22, height: 22, padding: 0,
                          borderRadius: 11, border: 'none', cursor: 'pointer', fontSize: 14, lineHeight: '22px',
                          background: 'var(--vkui--color_background_modal)', boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </Div>
              )}
            </Group>
            </>
          ) : (
            <Group
              header={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>Подзадачи</span>
                  <Caption style={{ color: 'var(--vkui--color_text_secondary)' }}>
                    {doneSubs}/{subs.length} выполнено
                  </Caption>
                </div>
              }
            >
              <Div>
                <Button size="m" mode="primary" stretched before={<Icon24Add />} onClick={openCreateSub}>
                  Новая подзадача
                </Button>
              </Div>
              {subs.length === 0 ? (
                <Div style={{ textAlign: 'center', padding: 24 }}>
                  <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Нет подзадач</Text>
                </Div>
              ) : (
                subs.map((s) => (
                  <Cell
                    key={s.id}
                    before={<Checkbox checked={s.is_complete} onChange={() => void toggleSub(s)} />}
                    after={
                      <div style={{ display: 'flex', gap: 2 }}>
                        <IconButton label="Изменить" onClick={() => openEditSub(s)}>
                          <Icon24Write />
                        </IconButton>
                        <IconButton label="Удалить" onClick={() => void deleteSub(s)}>
                          <Icon24Delete />
                        </IconButton>
                      </div>
                    }
                    subtitle={s.note || undefined}
                    style={s.is_complete ? { opacity: 0.55 } : undefined}
                  >
                    <span style={s.is_complete ? { textDecoration: 'line-through' } : undefined}>{s.name}</span>
                  </Cell>
                ))
              )}
            </Group>
          )}
        </>
      )}

      <ModalRoot activeModal={modal === 'subtask' ? sheetId('subtask-form', openToken) : null}>
        <ResizableModalPage openToken={openToken} id="subtask-form" onClose={() => { setModal(null); setEditingSub(null); }} settlingHeight={70}>
          <ModalPageHeader>{editingSub ? 'Редактировать подзадачу' : 'Новая подзадача'}</ModalPageHeader>
          <DragHandle />
          <Group>
            <FormItem top="Название *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </FormItem>
            <FormItem top="Статус">
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="s" mode={!form.is_complete ? 'primary' : 'secondary'} onClick={() => setForm({ ...form, is_complete: false })}>
                  В ожидании
                </Button>
                <Button size="s" mode={form.is_complete ? 'primary' : 'secondary'} onClick={() => setForm({ ...form, is_complete: true })}>
                  Выполнено
                </Button>
              </div>
            </FormItem>
            <FormItem top="Заметка">
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </FormItem>
            <Div style={{ display: 'flex', gap: 8 }}>
              <Button size="m" mode="primary" onClick={() => void saveSubtask()} disabled={!form.name.trim()}>
                Сохранить
              </Button>
              <Button size="m" mode="secondary" onClick={() => { setModal(null); setEditingSub(null); }}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      {confirmAtt && (
        <Alert
          actions={[
            {
              title: 'Удалить',
              mode: 'destructive',
              autoCloseDisabled: true,
              action: () => {
                const a = confirmAtt;
                setConfirmAtt(null);
                if (a) void deleteAtt(a);
              },
            },
            { title: 'Отмена', mode: 'cancel' },
          ]}
          actionsLayout="vertical"
          onClose={() => setConfirmAtt(null)}
          onClosed={() => setConfirmAtt(null)}
          title="Удалить вложение?"
          description="Файл будет удалён из задачи."
        />
      )}

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)} before={<Icon24Cancel />}>
          {snack}
        </Snackbar>
      )}
      <Div style={{ height: 24 }} />
    </Panel>
  );
}
